import type { SupportedLocale } from '@erp/shared';

export const appearanceTranslations = {
  de: {
    appearance: {
      title: 'Oberfläche',
      description:
        'Wählen Sie Ihre persönliche Arbeitsweise. Offene Ansichten und Eingaben bleiben beim Wechsel erhalten.',
      classic: 'Klassisch',
      classicDescription:
        'Die vertraute Oberfläche mit kompakter Navigation. Standard und verlässlicher Rückfall.',
      worlds: 'Arbeitswelten',
      worldsDescription:
        'Fachbereiche mit dunkler Navigation, klaren Farben und hervorgehobenen Aktionen.',
      dossier: 'Kundenakte',
      dossierDescription: 'Warme Gestaltung mit vertrauten Registern und gegliederten Akten.',
      workbench: 'Werkbank',
      workbenchDescription:
        'Passende Liste und Datensatz nebeneinander bearbeiten. Auf kleinen Bildschirmen untereinander.',
      active: 'Aktiv',
      storage:
        'Die Auswahl gilt für Ihren Benutzer und Mandanten in diesem Browser. Entwürfe werden nicht dauerhaft gespeichert.',
      colorMode: 'Hell / Dunkel / System',
    },
    workTabs: {
      tools: 'Suche, Filter & Ansichten',
      label: 'Arbeitsreiter',
      close: '{{name}} schließen',
      unsavedTitle: 'Ungespeicherte Eingaben',
      unsavedDescription:
        '„{{name}}“ enthält ungespeicherte Eingaben. Beim Schließen werden diese verworfen.',
      keep: 'Weiterarbeiten',
      discard: 'Verwerfen und schließen',
      record: 'Datensatz',
      new: {
        customer: 'Neuer Kunde',
        prospect: 'Neuer Interessent',
        supplier: 'Neuer Lieferant',
        product: 'Neuer Artikel',
        document: 'Neuer Beleg',
      },
    },
    workAreas: {
      partners: 'Kunden & Kontakte',
      products: 'Artikel & Preise',
      sales: 'Verkauf',
      purchasing: 'Einkauf',
      stock: 'Lager & Lieferung',
      accounting: 'Buchhaltung',
      other: 'Weitere Bereiche',
    },
  },
  en: {
    appearance: {
      title: 'Appearance',
      description: 'Choose how you work. Open views and entries are kept when switching.',
      classic: 'Classic',
      classicDescription:
        'The familiar interface with compact navigation. Default and reliable fallback.',
      worlds: 'Work areas',
      worldsDescription:
        'Business areas with dark navigation, clear colours and prominent actions.',
      dossier: 'Customer file',
      dossierDescription: 'Warm styling with familiar registers and structured files.',
      workbench: 'Workbench',
      workbenchDescription:
        'Work with the matching list and record side by side. Stacked on small screens.',
      active: 'Active',
      storage:
        'Your selection applies to your user and tenant in this browser. Drafts are not stored permanently.',
      colorMode: 'Light / Dark / System',
    },
    workTabs: {
      tools: 'Search, filters & views',
      label: 'Work tabs',
      close: 'Close {{name}}',
      unsavedTitle: 'Unsaved changes',
      unsavedDescription: '“{{name}}” contains unsaved changes. Closing it discards these entries.',
      keep: 'Keep working',
      discard: 'Discard and close',
      record: 'Record',
      new: {
        customer: 'New customer',
        prospect: 'New prospect',
        supplier: 'New supplier',
        product: 'New product',
        document: 'New document',
      },
    },
    workAreas: {
      partners: 'Customers & contacts',
      products: 'Products & prices',
      sales: 'Sales',
      purchasing: 'Purchasing',
      stock: 'Stock & delivery',
      accounting: 'Accounting',
      other: 'Other areas',
    },
  },
  fr: {
    appearance: {
      title: 'Interface',
      description:
        'Choisissez votre façon de travailler. Les vues et les saisies ouvertes sont conservées lors du changement.',
      classic: 'Classique',
      classicDescription:
        'L’interface familière avec une navigation compacte. Choix initial et solution de repli fiable.',
      worlds: 'Espaces métier',
      worldsDescription:
        'Domaines métier avec navigation sombre, couleurs distinctes et actions mises en avant.',
      dossier: 'Dossier client',
      dossierDescription:
        'Présentation chaleureuse avec des registres familiers et des dossiers structurés.',
      workbench: 'Établi',
      workbenchDescription:
        'Travaillez avec la liste correspondante et la fiche côte à côte, ou empilées sur petit écran.',
      active: 'Active',
      storage:
        'Le choix s’applique à votre utilisateur et à votre entreprise dans ce navigateur. Les brouillons ne sont pas conservés durablement.',
      colorMode: 'Clair / Sombre / Système',
    },
    workTabs: {
      tools: 'Recherche, filtres et vues',
      label: 'Onglets de travail',
      close: 'Fermer {{name}}',
      unsavedTitle: 'Saisies non enregistrées',
      unsavedDescription:
        '« {{name}} » contient des saisies non enregistrées. La fermeture les supprimera.',
      keep: 'Continuer à travailler',
      discard: 'Abandonner et fermer',
      record: 'Fiche',
      new: {
        customer: 'Nouveau client',
        prospect: 'Nouveau prospect',
        supplier: 'Nouveau fournisseur',
        product: 'Nouvel article',
        document: 'Nouveau document',
      },
    },
    workAreas: {
      partners: 'Clients et contacts',
      products: 'Articles et prix',
      sales: 'Ventes',
      purchasing: 'Achats',
      stock: 'Stock et livraison',
      accounting: 'Comptabilité',
      other: 'Autres domaines',
    },
  },
  es: {
    appearance: {
      title: 'Interfaz',
      description:
        'Elija cómo trabajar. Las vistas y los datos introducidos se conservan al cambiar.',
      classic: 'Clásica',
      classicDescription:
        'La interfaz conocida con navegación compacta. Opción inicial y alternativa fiable.',
      worlds: 'Áreas de trabajo',
      worldsDescription:
        'Áreas de negocio con navegación oscura, colores claros y acciones destacadas.',
      dossier: 'Expediente de cliente',
      dossierDescription: 'Diseño cálido con registros conocidos y expedientes estructurados.',
      workbench: 'Banco de trabajo',
      workbenchDescription:
        'Trabaje con la lista correspondiente y la ficha en paralelo, apiladas en pantallas pequeñas.',
      active: 'Activa',
      storage:
        'La selección se aplica a su usuario y empresa en este navegador. Los borradores no se guardan de forma permanente.',
      colorMode: 'Claro / Oscuro / Sistema',
    },
    workTabs: {
      tools: 'Búsqueda, filtros y vistas',
      label: 'Pestañas de trabajo',
      close: 'Cerrar {{name}}',
      unsavedTitle: 'Datos sin guardar',
      unsavedDescription: '«{{name}}» contiene datos sin guardar. Al cerrar se descartarán.',
      keep: 'Seguir trabajando',
      discard: 'Descartar y cerrar',
      record: 'Ficha',
      new: {
        customer: 'Nuevo cliente',
        prospect: 'Nuevo cliente potencial',
        supplier: 'Nuevo proveedor',
        product: 'Nuevo artículo',
        document: 'Nuevo documento',
      },
    },
    workAreas: {
      partners: 'Clientes y contactos',
      products: 'Artículos y precios',
      sales: 'Ventas',
      purchasing: 'Compras',
      stock: 'Almacén y entrega',
      accounting: 'Contabilidad',
      other: 'Otras áreas',
    },
  },
  tr: {
    appearance: {
      title: 'Arayüz',
      description: 'Çalışma biçiminizi seçin. Geçiş sırasında açık görünümler ve girişler korunur.',
      classic: 'Klasik',
      classicDescription:
        'Kompakt gezinmeli alışılmış arayüz. Başlangıç seçeneği ve güvenilir yedek görünüm.',
      worlds: 'İş alanları',
      worldsDescription: 'Koyu gezinme, belirgin renkler ve öne çıkan eylemlerle iş alanları.',
      dossier: 'Müşteri dosyası',
      dossierDescription: 'Alışılmış kayıt düzeni ve yapılandırılmış dosyalarla sıcak tasarım.',
      workbench: 'Çalışma tezgâhı',
      workbenchDescription:
        'İlgili liste ve kaydı yan yana düzenleyin. Küçük ekranlarda alt alta gösterilir.',
      active: 'Etkin',
      storage:
        'Seçim bu tarayıcıda kullanıcı ve şirketinize özeldir. Taslaklar kalıcı olarak saklanmaz.',
      colorMode: 'Açık / Koyu / Sistem',
    },
    workTabs: {
      tools: 'Arama, filtreler ve görünümler',
      label: 'Çalışma sekmeleri',
      close: '{{name}} sekmesini kapat',
      unsavedTitle: 'Kaydedilmemiş girişler',
      unsavedDescription:
        '“{{name}}” kaydedilmemiş girişler içeriyor. Kapatıldığında bu girişler silinir.',
      keep: 'Çalışmaya devam et',
      discard: 'Vazgeç ve kapat',
      record: 'Kayıt',
      new: {
        customer: 'Yeni müşteri',
        prospect: 'Yeni aday müşteri',
        supplier: 'Yeni tedarikçi',
        product: 'Yeni ürün',
        document: 'Yeni belge',
      },
    },
    workAreas: {
      partners: 'Müşteriler ve kişiler',
      products: 'Ürünler ve fiyatlar',
      sales: 'Satış',
      purchasing: 'Satın alma',
      stock: 'Stok ve teslimat',
      accounting: 'Muhasebe',
      other: 'Diğer alanlar',
    },
  },
} satisfies Record<SupportedLocale, Record<string, unknown>>;
